import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { searchApi, noteApi } from '../utils/api';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Card, CardBody, Row, Col, Form, Label, Input, Button, ButtonGroup, Spinner, Alert } from 'reactstrap';
import {
    Search,
    RotateCcw,
    Paperclip,
    Tag,
    Clock,
    CheckCircle2,
    Trash2,
    RotateCw,
    List,
    Grid3X3,
    LayoutGrid,
    Plus
} from 'lucide-react';
import NoteCard from '../components/NoteCard';

const AdvancedSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [filters, setFilters] = useState({
        q: '',
        tags: '',
        hasAttachments: false,
        anyTags: false,
        completed: null,
        createdFrom: '',
        createdTo: '',
        updatedFrom: '',
        updatedTo: '',
        targetFrom: '',
        targetTo: ''
    });

    const [results, setResults] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [deletedCount, setDeletedCount] = useState(0);
    
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [viewMode, setViewMode] = useState(localStorage.getItem('sb_view_mode') || 'grid');

    // Automatyczne odświeżenie wyników przy zmianie trybu widoku
    useEffect(() => {
        if (results.length > 0) {
            executeSearch(0, true);
        }
    }, [viewMode]);

    const changeViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('sb_view_mode', mode);
    };

    const handleSearchClick = (e) => {
        if (e) e.preventDefault();
        const params = {};
        if (filters.q) params.q = filters.q;
        if (filters.tags) params.tags = filters.tags;
        if (filters.hasAttachments) params.hasAttachments = 'true';
        if (filters.anyTags) params.anyTags = 'true';
        if (filters.completed !== null) params.completed = filters.completed.toString();
        if (filters.createdFrom) params.createdFrom = filters.createdFrom;
        if (filters.createdTo) params.createdTo = filters.createdTo;
        if (filters.updatedFrom) params.updatedFrom = filters.updatedFrom;
        if (filters.updatedTo) params.updatedTo = filters.updatedTo;
        if (filters.targetFrom) params.targetFrom = filters.targetFrom;
        if (filters.targetTo) params.targetTo = filters.targetTo;
        
        if (Object.keys(params).length === 0) {
            params.showAll = 'true';
        }
        
        params.page = '0';
        setSearchParams(params);
    };

    const handleClearFilters = () => {
        setFilters({
            q: '',
            tags: '',
            hasAttachments: false,
            anyTags: false,
            completed: null,
            createdFrom: '',
            createdTo: '',
            updatedFrom: '',
            updatedTo: '',
            targetFrom: '',
            targetTo: ''
        });
        setResults([]);
        setTotalElements(0);
        setSelectedIds([]);
        setSearchParams({ page: '0' });
    };

    useEffect(() => {
        const q = searchParams.get('q');
        const tags = searchParams.get('tags');
        const hasAtt = searchParams.get('hasAttachments');
        const anyT = searchParams.get('anyTags');
        const comp = searchParams.get('completed');
        const cf = searchParams.get('createdFrom') || searchParams.get('from'); 
        const ct = searchParams.get('createdTo') || searchParams.get('to');     
        const uf = searchParams.get('updatedFrom');
        const ut = searchParams.get('updatedTo');
        const tf = searchParams.get('targetFrom');
        const tt = searchParams.get('targetTo');
        const showAll = searchParams.get('showAll');
        const p = parseInt(searchParams.get('page') || '0');

        const initialFilters = {
            q: q || '',
            tags: tags || '',
            hasAttachments: hasAtt === 'true',
            anyTags: anyT === 'true',
            completed: comp === 'true' ? true : (comp === 'false' ? false : null),
            createdFrom: cf || '',
            createdTo: ct || '',
            updatedFrom: uf || '',
            updatedTo: ut || '',
            targetFrom: tf || '',
            targetTo: tt || ''
        };
        
        setFilters(initialFilters);

        if (q || tags || hasAtt === 'true' || anyT === 'true' || comp !== null || cf || ct || uf || ut || tf || tt || showAll === 'true') {
            // Wywołujemy wyszukiwanie z jawnymi parametrami, aby ominąć asynchroniczność stanu
            executeSearchWithFilters(p, initialFilters, true);
        } else {
            setResults([]);
            setTotalElements(0);
            setHasMore(false);
        }
    }, [searchParams]);

    // Dodajemy funkcję pomocniczą, która akceptuje filtry jako argument (do początkowego ładowania)
    const executeSearchWithFilters = async (pageNum, filterSet, replace = false) => {
        setLoading(true);
        if (replace) {
            setResults([]);
            setHasMore(false);
        }

        try {
            // Optymalizacja rozmiaru strony
            let size = 12;
            if (viewMode === 'small') size = 24;
            if (viewMode === 'list') size = 20;
            
            // Przygotowujemy parametry - wykluczamy puste stringi i null/undefined
            const params = {
                page: pageNum,
                size: size
            };
            
            if (filterSet.q && filterSet.q.trim() !== '' && filterSet.q !== 'null') params.q = filterSet.q;
            if (filterSet.tags && filterSet.tags.trim() !== '') {
                params.tags = filterSet.tags.split(',').map(t => t.trim()).filter(t => t !== '');
            }
            if (filterSet.hasAttachments === true) params.hasAttachments = true;
            if (filterSet.anyTags === true) params.anyTags = true;
            if (filterSet.completed !== null) params.completed = filterSet.completed;
            if (filterSet.createdFrom) {
                params.createdFrom = filterSet.createdFrom.includes(':') && filterSet.createdFrom.split(':').length === 2 
                    ? `${filterSet.createdFrom}:00` : filterSet.createdFrom;
            }
            if (filterSet.createdTo) {
                params.createdTo = filterSet.createdTo.includes(':') && filterSet.createdTo.split(':').length === 2 
                    ? `${filterSet.createdTo}:00` : filterSet.createdTo;
            }
            if (filterSet.updatedFrom) {
                params.updatedFrom = filterSet.updatedFrom.includes(':') && filterSet.updatedFrom.split(':').length === 2 
                    ? `${filterSet.updatedFrom}:00` : filterSet.updatedFrom;
            }
            if (filterSet.updatedTo) {
                params.updatedTo = filterSet.updatedTo.includes(':') && filterSet.updatedTo.split(':').length === 2 
                    ? `${filterSet.updatedTo}:00` : filterSet.updatedTo;
            }
            if (filterSet.targetFrom) {
                params.targetFrom = `${filterSet.targetFrom.substring(0, 10)}T00:00:00`;
            }
            if (filterSet.targetTo) {
                params.targetTo = `${filterSet.targetTo.substring(0, 10)}T23:59:59`;
            }

            const res = await searchApi.get('', { params });
            
            const content = res.data.content || (Array.isArray(res.data) ? res.data : []);
            
            let total = 0;
            if (res.data.page && res.data.page.totalElements !== undefined) {
                total = res.data.page.totalElements;
            } else if (res.data.totalElements !== undefined) {
                total = res.data.totalElements;
            }
            setTotalElements(total);

            let isLast = true;
            if (res.data.page) {
                isLast = res.data.page.number + 1 >= res.data.page.totalPages;
            } else if (res.data.last !== undefined) {
                isLast = res.data.last;
            } else {
                isLast = content.length < size;
            }

            if (replace) {
                setResults(content);
                setSelectedIds([]);
                setHasMore(!isLast);
            } else {
                setResults(prev => {
                    const updated = [...prev, ...content];
                    setHasMore(!isLast);
                    return updated;
                });
            }
            
            setPage(pageNum);
        } catch (err) {
            console.error("Search failed:", err);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    // Oryginalna funkcja teraz deleguje do nowej wersji, używając aktualnego stanu
    const executeSearch = async (pageNum, replace = false) => {
        return executeSearchWithFilters(pageNum, filters, replace);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleCardClick = (id) => {
        if (selectedIds.length > 0) {
            toggleSelect(id);
        } else {
            navigate(`/edit/${id}`);
        }
    };

    const handleDeleteSelected = async () => {
        const count = selectedIds.length;
        if (window.confirm(t('confirm_delete_plural', { count }))) {
            try {
                await noteApi.delete('', { data: selectedIds });
                
                // Lokalna aktualizacja stanu dla natychmiastowego efektu
                setResults(prev => prev.filter(note => !selectedIds.includes(note.id)));
                setDeletedCount(count);
                setSelectedIds([]);
                
                window.dispatchEvent(new CustomEvent('notes-updated'));
                
                // Ukrycie alertu po 10 sekundach
                setTimeout(() => setDeletedCount(0), 10000);
            } catch (err) {
                console.error("Failed to delete notes", err);
            }
        }
    };

    const getColProps = () => {
        if (viewMode === 'list') return { xs: 12 };
        if (viewMode === 'small') return { xs: 6, sm: 4, md: 3, lg: 2 };
        return { xs: 12, sm: 6, lg: 4 };
    };

    return (
        <div className="container mt-4 pb-5 animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold mb-0">{t('advanced_search_title')}</h2>
            </div>

            <Card className="border-0 shadow-sm mb-5 p-2 bg-card-custom no-hover" style={{ borderRadius: '20px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSearchClick}>
                        <Row className="g-3 mb-4">
                            <Col md={8}>
                                <Label className="small fw-bold opacity-50 text-uppercase mb-2 d-block">{t('keywords')}</Label>
                                <Input className="rounded-3 border-custom bg-custom px-3" style={{ padding: '12px' }} placeholder={t('search_placeholder')} value={filters.q} onChange={e => setFilters({...filters, q: e.target.value})} />
                            </Col>
                            <Col md={4}>
                                <Label className="small fw-bold opacity-50 text-uppercase mb-2 d-block">{t('tags_placeholder')}</Label>
                                <Input className="rounded-3 border-custom bg-custom px-3" style={{ padding: '12px' }} placeholder={t('tags_search_hint')} value={filters.tags} onChange={e => setFilters({...filters, tags: e.target.value})} />
                            </Col>
                        </Row>
                        
                        <Row className="mb-4">
                            <Col md={6} className="border-end border-custom">
                                <Label className="small fw-bold opacity-50 text-uppercase mb-3 d-block">
                                    📅 {t('created_date_range')}
                                </Label>
                                <Row className="g-2">
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_from')}</Label>
                                        <Input key={`cf-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.createdFrom} onChange={e => setFilters({...filters, createdFrom: e.target.value})} />
                                    </Col>
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_to')}</Label>
                                        <Input key={`ct-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.createdTo} onChange={e => setFilters({...filters, createdTo: e.target.value})} />
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={6} className="ps-md-4">
                                <Label className="small fw-bold opacity-50 text-uppercase mb-3 d-block">
                                    ✍️ {t('updated_date_range')}
                                </Label>
                                <Row className="g-2">
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_from')}</Label>
                                        <Input key={`uf-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.updatedFrom} onChange={e => setFilters({...filters, updatedFrom: e.target.value})} />
                                    </Col>
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_to')}</Label>
                                        <Input key={`ut-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.updatedTo} onChange={e => setFilters({...filters, updatedTo: e.target.value})} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col md={6} className="border-end border-custom">
                                <Label className="small fw-bold opacity-50 text-uppercase mb-3 d-block">
                                    {t('deadline_target')}
                                </Label>
                                <Row className="g-2">
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_from')}</Label>
                                        <Input key={`tf-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.targetFrom} onChange={e => setFilters({...filters, targetFrom: e.target.value})} />
                                    </Col>
                                    <Col sm={6}>
                                        <Label className="small opacity-75 mb-1">{t('date_to')}</Label>
                                        <Input key={`tt-${i18n.language}`} type="datetime-local" className="rounded-3 border-custom bg-custom shadow-sm" value={filters.targetTo} onChange={e => setFilters({...filters, targetTo: e.target.value})} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 bg-custom p-3 rounded-4 border-custom">
                            <div className="d-flex flex-wrap gap-2">
                                <Button 
                                    color="primary" 
                                    outline={!filters.hasAttachments}
                                    size="sm"
                                    className="rounded-pill px-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                                    onClick={() => setFilters({...filters, hasAttachments: !filters.hasAttachments})}
                                >
                                    <Paperclip size={15} />
                                    <span>{t('with_attachments')}</span>
                                </Button>
                                <Button 
                                    color="success" 
                                    outline={!filters.anyTags}
                                    size="sm"
                                    className="rounded-pill px-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                                    onClick={() => setFilters({...filters, anyTags: !filters.anyTags})}
                                >
                                    <Tag size={15} />
                                    <span>{t('with_any_tags')}</span>
                                </Button>
                                <Button 
                                    color="warning" 
                                    outline={filters.completed !== false}
                                    size="sm"
                                    className="rounded-pill px-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                                    onClick={() => setFilters({...filters, completed: filters.completed === false ? null : false})}
                                >
                                    <Clock size={15} />
                                    <span>{t('to_do')}</span>
                                </Button>
                                <Button 
                                    color="success" 
                                    outline={filters.completed !== true}
                                    size="sm"
                                    className="rounded-pill px-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                                    onClick={() => setFilters({...filters, completed: filters.completed === true ? null : true})}
                                >
                                    <CheckCircle2 size={15} />
                                    <span>{t('completed')}</span>
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                                <Button color="primary" className="fw-bold rounded-pill shadow-sm px-4 py-2 flex-grow-1 d-inline-flex align-items-center justify-content-center gap-2">
                                    {loading && results.length === 0 ? <Spinner size="sm" /> : <><Search size={16} /> <span>{t('search_button')}</span></>}
                                </Button>
                                <Button type="button" color="light" onClick={handleClearFilters} className="fw-bold rounded-pill px-3 py-2 border shadow-sm btn-theme-aware d-inline-flex align-items-center gap-1">
                                    <RotateCcw size={16} />
                                    <span>{t('clear_filters')}</span>
                                </Button>
                            </div>
                        </div>
                    </Form>
                </CardBody>
            </Card>

            <div className="results">
                {deletedCount > 0 && (
                    <Alert color="info" toggle={() => setDeletedCount(0)} className="shadow-sm border-0 rounded-4 mb-4 fade show animate-fade-in">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <Trash2 size={20} />
                                <span>
                                    {t('deletion_success_message', { count: deletedCount })}
                                </span>
                            </div>
                            <Button 
                                size="sm" 
                                color="info" 
                                outline 
                                onClick={() => { executeSearch(0, true); setDeletedCount(0); }}
                                className="rounded-pill fw-bold border-2 px-3 d-inline-flex align-items-center gap-1"
                            >
                                <RotateCw size={14} />
                                <span>{t('refresh_list')}</span>
                            </Button>
                        </div>
                    </Alert>
                )}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 sticky-top p-3 bg-custom-blur rounded-4 border-custom shadow-sm" style={{ top: '62px', zIndex: 10 }}>
                    <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0 fw-bold px-2">
                            {loading && results.length === 0 ? t('searching') : (
                                <>{t('search_results')} <span className="text-primary">{totalElements}</span></>
                            )}
                        </h4>
                        {selectedIds.length > 0 && (
                            <span className="text-primary fw-bold">({t('selected_count', { count: selectedIds.length })})</span>
                        )}
                    </div>
                    
                    <div className="d-flex align-items-center gap-2">
                        {selectedIds.length > 0 && (
                            <Button color="danger" outline onClick={handleDeleteSelected} size="sm" className="rounded-pill px-3 shadow-sm fw-bold d-inline-flex align-items-center gap-1">
                                <Trash2 size={15} />
                                <span>{t('delete_selected')}</span>
                            </Button>
                        )}
                        <ButtonGroup size="sm" className="shadow-sm">
                            <Button color="light" active={viewMode === 'list'} onClick={() => changeViewMode('list')} className="btn-theme-aware border d-inline-flex align-items-center px-2 py-1" title={t('view_list')}>
                                <List size={16} />
                            </Button>
                            <Button color="light" active={viewMode === 'small'} onClick={() => changeViewMode('small')} className="btn-theme-aware border d-inline-flex align-items-center px-2 py-1" title={t('view_small_grid')}>
                                <Grid3X3 size={16} />
                            </Button>
                            <Button color="light" active={viewMode === 'grid'} onClick={() => changeViewMode('grid')} className="btn-theme-aware border d-inline-flex align-items-center px-2 py-1" title={t('view_large_grid')}>
                                <LayoutGrid size={16} />
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
                
                <Row className={viewMode === 'list' ? 'g-2' : 'g-3 g-md-4'}>
                    {results.map(result => (
                        <Col key={result.id} {...getColProps()}>
                            <NoteCard 
                                note={result}
                                isSelected={selectedIds.includes(result.id)}
                                onSelect={toggleSelect}
                                onClick={handleCardClick}
                                viewMode={viewMode}
                            />
                        </Col>
                    ))}
                </Row>
            </div>

            {/* Load More or End of Results */}
            <div className="text-center mt-5 mb-5">
                {loading ? (
                    <Spinner color="primary" />
                ) : hasMore ? (
                    <Button color="outline-primary" size="lg" outline onClick={() => executeSearch(page + 1)} className="rounded-pill px-5">
                        {t('show_more')}
                    </Button>
                ) : results.length > 0 && (
                    <div className="animate-fade-in py-4 border-top border-custom">
                        <p className="text-muted small mb-3">✨ {t('reached_end_of_brain')}</p>
                        <Button color="primary" size="lg" onClick={() => navigate('/new-note')} className="rounded-pill px-4 py-2 shadow-sm fw-bold d-inline-flex align-items-center gap-2">
                            <Plus size={18} />
                            <span>{t('new_note_title')}</span>
                        </Button>
                    </div>
                )}
            </div>
            
            <style>
                {`
                .cursor-pointer { cursor: pointer; }
                .bg-card-custom { background-color: var(--sb-card-bg)!important; }
                .bg-custom { background-color: var(--sb-bg)!important; }
                .bg-custom-blur { 
                    background-color: var(--sb-bg)!important; 
                    backdrop-filter: blur(10px);
                    opacity: 0.95;
                }
                .border-custom { border-color: var(--sb-border)!important; }
                .text-theme { color: var(--sb-text)!important; }
                .btn-theme-aware { 
                    background-color: var(--sb-card-bg)!important; 
                    color: var(--sb-text)!important; 
                    border-color: var(--sb-border)!important; 
                }
                .btn-theme-aware:hover {
                    background-color: var(--sb-bg)!important;
                }
                .no-hover:hover {
                    transform: none !important;
                    box-shadow: var(--sb-shadow) !important;
                    border-color: var(--sb-border) !important;
                }
                `}
            </style>
        </div>
    );
};

export default AdvancedSearch;
